/**
 * Reports Page — Generate + List + Calendar
 *
 * - Generate report button (select fund + period)
 * - Report list (existing reports)
 * - Compliance calendar
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Download,
  Plus,
  Calendar,
  X,
  ChevronDown,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';
import type { Report, CalendarEvent, Fund } from '@/types';

type TabKey = 'reports' | 'calendar';

export function ReportsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('reports');
  const [showGenerate, setShowGenerate] = useState(false);
  const [calendarFundId, setCalendarFundId] = useState('');

  const reportsQuery = useQuery<Report[]>({
    queryKey: ['ic', 'reports'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/reports/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const calendarQuery = useQuery<CalendarEvent[]>({
    queryKey: ['ic', 'reports', 'calendar', calendarFundId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/reports/calendar/${calendarFundId}`);
      return res.data?.data ?? res.data ?? [];
    },
    enabled: activeTab === 'calendar' && !!calendarFundId,
  });

  const reports = reportsQuery.data ?? [];
  const funds = fundsQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">{t('page.reports')}</h1>
          <p className="text-xs text-ic-text-tertiary">Generate and manage compliance reports</p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('action.generateReport')}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'reports' ? 'border-ic-accent text-ic-accent' : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
            }`}
          >
            <FileText className="h-3.5 w-3.5" />
            Reports
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'calendar' ? 'border-ic-accent text-ic-accent' : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
            }`}
          >
            <Calendar className="h-3.5 w-3.5" />
            Calendar
          </button>
        </div>
      </div>

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {reportsQuery.isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
            </div>
          ) : reportsQuery.isError ? (
            <div className="flex h-48 items-center justify-center">
              <p className="text-xs text-red-400">{t('error.loadFailed')}</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3">
              <FileText className="h-5 w-5 text-ic-text-tertiary" />
              <p className="text-xs text-ic-text-tertiary">No reports generated yet.</p>
              <button
                onClick={() => setShowGenerate(true)}
                className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Generate Report
              </button>
            </div>
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.name')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.fund')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.type')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.period')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.status')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.generated')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">
                      {report.name ?? report.title ?? (report.regulation && report.filing_type ? `${report.regulation.toUpperCase()} ${report.filing_type.replace(/_/g, ' ')}` : report.filing_type ?? '--')}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{report.fund_name ?? '--'}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label={report.filing_type ?? report.report_type ?? report.type ?? '--'} variant="info" />
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{report.reporting_period ?? report.period ?? '--'}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={report.status ? formatStatus(report.status, t) : '--'}
                        variant={(report.status ?? '') === 'completed' ? 'success' : (report.status ?? '') === 'failed' ? 'danger' : 'default'}
                      />
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                      {(report.generated_at ?? report.created_at) ? format(parseISO(report.generated_at ?? report.created_at), 'dd MMM yyyy HH:mm') : '--'}
                    </td>
                    <td className="py-2.5 px-4">
                      {report.download_url && (
                        <a
                          href={report.download_url}
                          className="text-ic-accent hover:text-ic-accent-hover transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <select
              value={calendarFundId}
              onChange={(e) => setCalendarFundId(e.target.value)}
              className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              <option value="">Select a fund...</option>
              {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
          </div>

          {!calendarFundId ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
              <p className="text-xs text-ic-text-tertiary">Select a fund to view compliance calendar.</p>
            </div>
          ) : calendarQuery.isLoading ? (
            <div className="rounded-lg border border-ic-border bg-ic-card p-4 space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
            </div>
          ) : calendarQuery.isError ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
              <p className="text-xs text-red-400">Failed to load calendar events.</p>
            </div>
          ) : (calendarQuery.data ?? []).length === 0 ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
              <p className="text-xs text-ic-text-tertiary">No upcoming events for this fund.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-ic-border bg-ic-card divide-y divide-ic-border/50">
              {(calendarQuery.data ?? []).map((event, idx) => {
                const eventDate = event.deadline ?? event.date ?? event.due_date;
                const eventTitle = event.filing ?? event.title ?? event.name ?? '--';
                const eventType = event.regulation ?? event.type ?? event.event_type ?? '--';
                return (
                <div key={event.id ?? idx} className="flex items-center gap-4 px-4 py-3">
                  <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-ic-accent/10">
                    <span className="text-[10px] font-medium text-ic-accent">
                      {(() => { try { return eventDate ? format(parseISO(eventDate), 'MMM') : '--'; } catch { return '--'; } })()}
                    </span>
                    <span className="text-xs font-bold text-ic-accent">
                      {(() => { try { return eventDate ? format(parseISO(eventDate), 'dd') : '--'; } catch { return '--'; } })()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-ic-text-primary">{eventTitle}</p>
                    {eventDate && (
                      <p className="text-[10px] text-ic-text-secondary">{eventDate}</p>
                    )}
                    <p className="text-[10px] text-ic-text-tertiary">
                      {eventType} {event.frequency ? `(${event.frequency})` : ''} {event.fund_name ? `- ${event.fund_name}` : ''}
                    </p>
                  </div>
                  {event.status && (
                    <StatusBadge
                      label={formatStatus(event.status, t)}
                      variant={event.status === 'completed' ? 'success' : event.status === 'overdue' ? 'danger' : 'default'}
                    />
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Generate Dialog */}
      {showGenerate && (
        <GenerateReportDialog
          funds={funds}
          onClose={() => setShowGenerate(false)}
          onGenerated={() => {
            setShowGenerate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'reports'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Generate Dialog ───────────────────────────────────────────

function GenerateReportDialog({ funds, onClose, onGenerated }: { funds: Fund[]; onClose: () => void; onGenerated: () => void }) {
  const { t } = useTranslation();
  const [fundId, setFundId] = useState('');
  const [reportType, setReportType] = useState('quarterly');
  const [period, setPeriod] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (payload: { fund_id: number; report_type: string; period: string }) => {
      const res = await apiClient.post('/api/v1/impact-core/reports/generate', payload);
      return res.data;
    },
    onSuccess: () => onGenerated(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail ?? 'Failed to generate report.');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!fundId) { setError('Select a fund'); return; }
    if (!period) { setError('Enter a period'); return; }
    mutation.mutate({ fund_id: Number(fundId), report_type: reportType, period });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">{t('action.generateReport')}</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{`${t('table.fund')} *`}</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
              <option value="">{t('form.selectFund')}</option>
              {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{t('form.reportType')}</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
              <option value="quarterly">{t('report.quarterly')}</option>
              <option value="annual">{t('report.annual')}</option>
              <option value="pai-statement">{t('report.paiStatement')}</option>
              <option value="taxonomy">{t('labels.euTaxonomy')}</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{`${t('table.period')} *`}</label>
            <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. FY2025, Q4 2025"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors">
            {mutation.isPending ? t('action.generate') + '...' : t('action.generate')}
          </button>
        </div>
      </div>
    </div>
  );
}
